import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContactAndSocialStep = ({ formData, updateFormData, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.contactEmail?.trim()) {
      newErrors.contactEmail = 'El correo de contacto es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contactEmail)) {
      newErrors.contactEmail = 'Correo electrónico inválido';
    }

    if (!formData?.contactPhone?.trim()) {
      newErrors.contactPhone = 'El teléfono de contacto es requerido';
    } else if (!/^(\+57)?[0-9]{10}$/?.test(formData?.contactPhone?.replace(/\s/g, ''))) {
      newErrors.contactPhone = 'Formato: +57 312 456 7890';
    }

    if (!formData?.whatsappNumber?.trim()) {
      newErrors.whatsappNumber = 'El número de WhatsApp es requerido';
    } else if (!/^(\+57)?[0-9]{10}$/?.test(formData?.whatsappNumber?.replace(/\s/g, ''))) {
      newErrors.whatsappNumber = 'Formato: +57 312 456 7890';
    }

    if (formData?.websiteUrl && !isValidUrl(formData?.websiteUrl)) {
      newErrors.websiteUrl = 'URL inválida. Debe incluir http:// o https://';
    }

    if (formData?.facebookUrl && !isValidSocialUrl(formData?.facebookUrl, 'facebook.com')) {
      newErrors.facebookUrl = 'URL de Facebook inválida';
    }

    if (formData?.instagramUrl && !isValidSocialUrl(formData?.instagramUrl, 'instagram.com')) {
      newErrors.instagramUrl = 'URL de Instagram inválida';
    }

    if (formData?.tiktokUrl && !isValidSocialUrl(formData?.tiktokUrl, 'tiktok.com')) {
      newErrors.tiktokUrl = 'URL de TikTok inválida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isValidSocialUrl = (url, ...platforms) => {
    if (!isValidUrl(url)) return false;
    const urlObj = new URL(url);
    return platforms?.some(platform => urlObj?.hostname?.includes(platform));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value?.replace(/\D/g, '');
    if (cleaned?.startsWith('57')) {
      const number = cleaned?.substring(2);
      if (number?.length <= 10) {
        return `+57 ${number?.substring(0, 3)} ${number?.substring(3, 6)} ${number?.substring(6, 10)}`?.trim();
      }
    } else if (cleaned?.length <= 10) {
      return `${cleaned?.substring(0, 3)} ${cleaned?.substring(3, 6)} ${cleaned?.substring(6, 10)}`?.trim();
    }
    return value;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Información de Contacto</h2>
        <p className="text-muted-foreground">Proporciona los canales de comunicación con tus clientes</p>
      </div>
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
          <Icon name="Phone" size={20} className="text-accent" />
          Contacto Directo
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Correo Electrónico de Contacto"
            type="email"
            required
            placeholder="contacto@tunegocio.com"
            value={formData?.contactEmail || ''}
            onChange={(e) => updateFormData({ contactEmail: e?.target?.value })}
            error={errors?.contactEmail}
            description="Correo principal para consultas"
          />

          <Input
            label="Teléfono de Contacto"
            type="tel"
            required
            placeholder="+57 312 456 7890"
            value={formData?.contactPhone || ''}
            onChange={(e) => {
              const formatted = formatPhoneNumber(e?.target?.value);
              updateFormData({ contactPhone: formatted });
            }}
            error={errors?.contactPhone}
            description="Número principal del negocio"
          />
        </div>

        <Input
          label="Número de WhatsApp Business"
          type="tel"
          required
          placeholder="+57 312 456 7890"
          value={formData?.whatsappNumber || ''}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e?.target?.value);
            updateFormData({ whatsappNumber: formatted });
          }}
          error={errors?.whatsappNumber}
          description="Número para atención por WhatsApp"
        />
      </div>
      <div className="border-t pt-6">
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Globe" size={20} className="text-accent" />
          Sitio Web y Redes Sociales
        </h3>

        <div className="space-y-6">
          <Input
            label="Sitio Web"
            type="url"
            placeholder="https://www.tunegocio.com"
            value={formData?.websiteUrl || ''}
            onChange={(e) => updateFormData({ websiteUrl: e?.target?.value })}
            error={errors?.websiteUrl}
            description="URL de tu sitio web (opcional)"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Facebook"
              type="url"
              placeholder="https://facebook.com/tunegocio"
              value={formData?.facebookUrl || ''}
              onChange={(e) => updateFormData({ facebookUrl: e?.target?.value })}
              error={errors?.facebookUrl}
              description="Página de Facebook (opcional)"
            />

            <Input
              label="Instagram"
              type="url"
              placeholder="https://instagram.com/tunegocio"
              value={formData?.instagramUrl || ''}
              onChange={(e) => updateFormData({ instagramUrl: e?.target?.value })}
              error={errors?.instagramUrl}
              description="Perfil de Instagram (opcional)"
            />
          </div>

          <Input
            label="TikTok"
            type="url"
            placeholder="https://tiktok.com/@tunegocio"
            value={formData?.tiktokUrl || ''}
            onChange={(e) => updateFormData({ tiktokUrl: e?.target?.value })}
            error={errors?.tiktokUrl}
            description="Perfil de TikTok (opcional)"
          />
        </div>
      </div>
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Visibilidad aumentada</p>
            <p>Completa toda tu información de contacto y redes sociales para que tus clientes puedan encontrarte fácilmente. Las empresas con perfiles completos reciben hasta 3 veces más interacciones.</p>
          </div>
        </div>
      </div>
      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-green-900">
            <p className="font-semibold mb-1">WhatsApp Business recomendado</p>
            <p>Usa WhatsApp Business para mejor gestión de consultas y catálogo de productos. Los clientes prefieren negocios con respuesta inmediata.</p>
          </div>
        </div>
      </div>
      <div className="flex gap-4 pt-6 border-t">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
        >
          Regresar
        </Button>

        <Button
          type="submit"
          variant="default"
          size="lg"
          iconName="ArrowRight"
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-accent to-orange-600 hover:shadow-lg transform hover:scale-105 transition-all"
        >
          Continuar
        </Button>
      </div>
    </form>
  );
};

export default ContactAndSocialStep;